// Assuming we use the Button component inside, we can reuse this type
import type { ReactElement, ReactNode } from 'react';

import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { zIndexes } from '../../constants';

/**
  @param hint - If supplied, will be displayed left of the right button
  @param leftSideComponents - If supplied, renders components on the far left of the footer
  @param rightSideComponents - If supplied, renders components on the far right of the footer
 */
interface ProcessFooterProps {
    hint?: string | ReactNode;
    leftSideComponents?: Array<ReactNode>;
    rightSideComponents?: Array<ReactNode>;
    hideShadow?: boolean;
    loading?: boolean;
}

const ProcessFooterLoadingState = (): ReactElement => (
    <>
        <Skeleton
            variant="rectangular"
            width={180}
            height={56}
        />
        <Stack
            sx={{
                flexDirection: 'row',
            }}
        >
            <Stack
                sx={{
                    alignItems: 'flex-end',
                }}
            >
                <Stack direction="row">
                    <Skeleton width={110} />
                    <Skeleton width={110} />
                </Stack>
                <Skeleton width={110} />
            </Stack>
            <Skeleton
                variant="rectangular"
                sx={{ marginLeft: 10 }}
                width={180}
                height={56}
            />
        </Stack>
    </>
);

export const ProcessFooter = ({
    hint,
    leftSideComponents,
    rightSideComponents,
    hideShadow = true,
    loading,
}: ProcessFooterProps): ReactElement => {
    const { shadows } = useTheme();

    return (
        <Stack
            direction="row"
            sx={{
                marginTop: 'auto',
                height: 80,
                minHeight: 80,
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: hideShadow ? 'none' : shadows[1],
                transition: 'box-shadow 0.2s ease-in-out',
                px: 10,
                zIndex: zIndexes.footers,
            }}
        >
            {loading ? (
                <ProcessFooterLoadingState />
            ) : (
                <>
                    <Stack
                        useFlexGap
                        sx={{
                            flexDirection: 'row',
                            gap: 5,
                        }}
                    >
                        {leftSideComponents?.map((c) => c)}
                    </Stack>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Stack
                            direction="column"
                            sx={{
                                paddingRight: 5,
                            }}
                        >
                            {hint}
                        </Stack>
                        <Stack
                            useFlexGap
                            sx={{
                                flexDirection: 'row',
                                gap: 5,
                            }}
                        >
                            {rightSideComponents?.map((c) => c)}
                        </Stack>
                    </Stack>
                </>
            )}
        </Stack>
    );
};
